import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import {
  CardItem as BaseCardItem,
  isClassCardItem,
  isCourseCardItem,
  isMeditationCardItem,
  isArticleCardItem,
} from '@yi/core';

import { isUserCreatedPracticeCardItem } from '~/types';

import {
  BaseCarousel,
  BaseCarouselComposerProps,
  BaseCarouselProps,
  createSlickSettings,
} from './BaseCarousel';
import {
  ArticleCard,
  ClassCard,
  CourseCard,
  MeditationCard,
  UserCreatedPracticeCard,
} from '../cards';
import {
  RecentSeriesActivityCard,
  Item as RecentSeriesActivityCardItem,
  isRecentSeriesEpisodeCardItem,
} from '../cards/RecentSeriesActivityCard';

const carouselHeaderCSS = css`
  line-height: 1;
  margin-bottom: 14px;
  text-align: left;
`;
type CardItem = BaseCardItem | RecentSeriesActivityCardItem;

export type MixCardsCarouselProps = BaseCarouselComposerProps<CardItem>;

const slickSettings = createSlickSettings({
  sm: {
    slidesToShow: 2,
    slidesToScroll: 1,
    dots: false,
  },
  md: {
    slidesToShow: 3,
    slidesToScroll: 2,
    dots: false,
  },

  lg: {
    slidesToShow: 4,
    slidesToScroll: 3,
    dots: false,
  },
  xl: {
    slidesToShow: 2,
    slidesToScroll: 1,
    dots: false,
  },
  xxl: {
    slidesToShow: 2,
    slidesToScroll: 1,
    dots: false,
  },
});

type Dimension = { width: number; height: number };

export const getMixCardRenderer: (
  dimension?: Dimension,
) => BaseCarouselProps<CardItem>['renderCard'] =
  (dimension?: Dimension) =>
  ({ item, ...rest }) => {
    if (isRecentSeriesEpisodeCardItem(item)) {
      return (
        <RecentSeriesActivityCard
          item={item}
          dimension={dimension ? dimension : { width: 282, height: 320 }}
          {...rest}
        />
      );
    } else if (isClassCardItem(item)) {
      // Currently ClassCard has fixed dimension.
      return <ClassCard item={item} {...rest} />;
    } else if (isCourseCardItem(item)) {
      return (
        <CourseCard
          item={item}
          dimension={dimension ? dimension : { width: 282, height: 320 }}
          {...rest}
        />
      );
    } else if (isMeditationCardItem(item)) {
      return (
        <MeditationCard
          item={item}
          dimension={dimension ? dimension : { width: 282, height: 320 }}
          {...rest}
        />
      );
    } else if (isArticleCardItem(item)) {
      return (
        <ArticleCard
          item={item}
          dimension={dimension ? dimension : { width: 282, height: 320 }}
          {...rest}
        />
      );
    } else if (isUserCreatedPracticeCardItem(item)) {
      return (
        <UserCreatedPracticeCard
          item={item}
          dimension={dimension ? dimension : { width: 282, height: 320 }}
          {...rest}
        />
      );
    }
    return null;
  };

export const MixCardsCarousel: React.FC<
  PropsWithChildren<MixCardsCarouselProps & { cardDimension?: Dimension }>
> = ({ cardDimension, ...props }) => {
  return (
    <BaseCarousel<CardItem>
      headerCSS={carouselHeaderCSS}
      containerStyles={{ padding: 0 }}
      minItems={1}
      slickSettings={slickSettings}
      button={null}
      getReactKey={(item: CardItem) => item.id}
      renderCard={getMixCardRenderer(cardDimension)}
      {...props}
    />
  );
};
